/// <amd-module name="Build/Admin/Pool/Sources/PoolAnalyticsSource" />

//We will need this eventually but for now we are using mock data. We will only need this once backend is done

import { AnalyticsODataVersion, ODataQueryOptions } from "Analytics/Common/ODataQueryOptions";
import { DateSKParser } from "Analytics/Common/Utilities/DateSKParser";
import { AnalyticsODataClientBase } from "Analytics/Shared/Common/Sources/AnalyticsODataClientBase";
import { Period, TrendBy } from "Analytics/Shared/Common/Types";
import { adjustDataForAllPeriod, getDateFilter, getLookBackDate, IDataForDate } from "Analytics/Shared/Common/Utils/DateAdjuster";
import { ITrendChartData, ITrendChartReportConfiguration } from "Analytics/Shared/TrendChart/Common/Types";
import { addDays, convertDateToDateString, getDate } from "Analytics/Shared/Utils/DateUtils";
import { PoolDataStrings, Visibility } from "Build/Admin/Pool/Types";
import { IVssPageContext } from "VSS/Platform/Context";

export interface IODataTaskAgentRequest {
    SamplingDateSK: number;
    SamplingHour?: number;
    MaxCount: number;
}

export interface IODataConcurrency {
    SamplingDate: string;
    TotalCount: number;
    OnlineCount: number;
}

export class PoolAnalyticsSource extends AnalyticsODataClientBase {
    constructor(_command: string, pageContext: IVssPageContext) {
        super(_command, pageContext);
    }

    public async _queryPublicPoolTrendData(confValues: ITrendChartReportConfiguration, poolId: number, hosted: boolean): Promise<ITrendChartData> {
        return await this._queryPoolTrendData(confValues, poolId, hosted, Visibility.Public);
    }

    public async _queryPrivatePoolTrendData(confValues: ITrendChartReportConfiguration, poolId: number, hosted: boolean): Promise<ITrendChartData> {
        return await this._queryPoolTrendData(confValues, poolId, hosted, Visibility.Private);
    }

    protected async _queryPoolTrendData(confValues: ITrendChartReportConfiguration, poolId: number, hosted: boolean, visibility: string) {
        const chartData: ITrendChartData = {
            primaryChartData: {
                [PoolDataStrings.RunningJobs]: [],
                [PoolDataStrings.Concurrency]: []
            },
            secondaryChartData: [],
            confValues: confValues
        };

        const runningJobsQueryOptions = this._getTaskAgentRequestsQueryOptions(confValues, poolId, hosted, visibility, true);

        /**
         * _odata/v4.0-preview/TaskAgentRequestSnapshots?
         *  $apply=filter(SamplingDateSK ge 20200923 AND PoolId eq 1 AND Project/ProjectVisibility eq 'Public' AND IsRunning eq true AND IsHosted eq true)
         *  /groupby((SamplingDateSK, SamplingHour, SamplingTime), aggregate($count as Count))
         *  /groupby((SamplingDateSK),aggregate($count as MaxCount))
         *  &$orderBy=SamplingDateSK asc
         */
        const runningJobsData = await this.queryOData(runningJobsQueryOptions);

        const adjustableRunningJobsData: IDataForDate<number>[] = [];

        (runningJobsData.value as IODataTaskAgentRequest[]).forEach(d => {
            const dateString =
                confValues.trendBy === TrendBy.Hours
                    ? this._convertSamplingHourToTimeString(d.SamplingHour)
                    : DateSKParser.parseDateSKAsDateString(d.SamplingDateSK);
            adjustableRunningJobsData.push({ data: d.MaxCount, date: dateString });
        });

        chartData.primaryChartData[PoolDataStrings.RunningJobs] = this._adjustData(
            adjustableRunningJobsData,
            confValues.trendBy,
            confValues.period,
            0
        ).map(value => {
            return { aggrValue: value.data!, date: value.date };
        });

        const queuedJobsQueryOptions = this._getTaskAgentRequestsQueryOptions(confValues, poolId, hosted, visibility, false);

        /**
         * _odata/v4.0-preview/TaskAgentRequestSnapshots?
         *  $apply=filter(SamplingDateSK ge 20200923 AND PoolId eq 1 AND Project/ProjectVisibility eq 'Public' AND IsRunning eq false AND IsHosted eq true)
         *  /groupby((SamplingDateSK, SamplingHour, SamplingTime), aggregate($count as Count))
         *  /groupby((SamplingDateSK),aggregate($count as MaxCount))
         *  &$orderBy=SamplingDateSK asc
         */
        const queuedJobsData = await this.queryOData(queuedJobsQueryOptions);

        const adjustableQueuedJobsData: IDataForDate<number>[] = [];

        (queuedJobsData.value as IODataTaskAgentRequest[]).forEach(d => {
            const dateString =
                confValues.trendBy === TrendBy.Hours
                    ? this._convertSamplingHourToTimeString(d.SamplingHour)
                    : DateSKParser.parseDateSKAsDateString(d.SamplingDateSK);
            adjustableQueuedJobsData.push({ data: d.MaxCount, date: dateString });
        });

        chartData.primaryChartData[PoolDataStrings.QueuedJobs] = this._adjustData(
            adjustableQueuedJobsData,
            confValues.trendBy,
            confValues.period,
            0
        ).map(value => {
            return { aggrValue: value.data!, date: value.date };
        });

        const concurrencyQueryOptions = this._getConcurrencyQueryOptions(confValues, poolId, hosted, visibility);

        /**
         * _odata/v4.0-preview/ParallelPipelineJobsSnapshot?
         *  $apply=filter(SamplingDate ge 2020-09-23 AND IsHosted eq false AND ParallelismTag eq 'Public')
         *  &$orderBy=SamplingDate asc
         */
        const concurrencyData = await this.queryOData(concurrencyQueryOptions);
        let adjustableConcurrencyData: IDataForDate<number>[] = [];

        (concurrencyData.value as IODataConcurrency[]).forEach(d => {
            adjustableConcurrencyData.push({
                data: hosted || visibility === Visibility.Private ? d.TotalCount : d.OnlineCount,
                date:
                    confValues.trendBy === TrendBy.Hours
                        ? this._convertSamplingHourToTimeString(new Date(d.SamplingDate).getHours())
                        : convertDateToDateString(new Date(d.SamplingDate))
            });
        });
        adjustableConcurrencyData = this._adjustData(adjustableConcurrencyData, confValues.trendBy, confValues.period, -1);

        chartData.primaryChartData[PoolDataStrings.Concurrency] = adjustableConcurrencyData.map((value, index) => {
            if (value.data == null || value.data === -1) {
                const prevConcurrencyIndex = index - 1;
                const prevConcurrency = adjustableConcurrencyData[prevConcurrencyIndex];

                let nextConcurrencyIndex = adjustableConcurrencyData.slice(index + 1).findIndex(x => x.data !== null && x.data !== -1);
                if (nextConcurrencyIndex !== -1) {
                    nextConcurrencyIndex += index + 1;
                }
                const nextConcurrency = adjustableConcurrencyData[nextConcurrencyIndex];

                if (nextConcurrency && nextConcurrency.data && nextConcurrency.data !== -1) {
                    if (prevConcurrency && prevConcurrency.data && prevConcurrency.data !== -1) {
                        value.data =
                            prevConcurrency.data + (nextConcurrency.data - prevConcurrency.data) / (nextConcurrencyIndex - prevConcurrencyIndex);
                    } else {
                        value.data = nextConcurrency.data;
                    }
                } else {
                    if (prevConcurrency && prevConcurrency.data && prevConcurrency.data !== -1) {
                        value.data = prevConcurrency.data;
                    } else {
                        value.data = 0;
                    }
                }
            }

            return { aggrValue: value.data, date: value.date };
        });

        return chartData;
    }

    // This code is the start of work for v2. Currently commented out for v1
    // public async _queryTopFivePipelinesData(
    //     queryOptions: ODataQueryOptions,
    //     confValues: ITrendChartReportConfiguration
    // ): Promise<IPipelinesConsumptionData[]> {
    //     return getTopFivePipelinesMockData(confValues);
    // }

    protected _getTaskAgentRequestsQueryOptions(
        confValues: ITrendChartReportConfiguration,
        poolId: number,
        hosted: boolean,
        visibility: string,
        isRunning: boolean
    ): ODataQueryOptions {
        const queryOptions = {
            entityType: c_TaskAgentResultEntityName,
            oDataVersion: AnalyticsODataVersion.v4Preview
        } as ODataQueryOptions;

        // AnalyticsODataClientBase._getProjectId throws an error if we are at collection level settings, catch it and ignore that option.
        try {
            const projectId = super._getProjectId();
            (queryOptions as ODataQueryOptions).project = projectId;
        } catch (e) {}

        const filterStr = this._getTaskAgentRequestsFilter(confValues, poolId, hosted, visibility, isRunning);

        let groupByStr = this._getTaskAgentRequestsInitialGrouping();
        groupByStr += `groupby((${this._getTaskAgentRequestsGrouping(confValues.trendBy)}),`;
        groupByStr += `aggregate(${this._getTaskAgentRequestsAggregation()}))`;

        queryOptions.$apply = `${filterStr}/${groupByStr}`;
        queryOptions.$orderby = `SamplingDateSK asc `;

        return queryOptions;
    }

    protected _getTaskAgentRequestsInitialGrouping(): string {
        return "groupby((SamplingDateSK, SamplingHour, SamplingTime), aggregate($count as Count))/";
    }

    protected _getTaskAgentRequestsGrouping(trendBy: TrendBy): string {
        return trendBy == TrendBy.Hours ? "SamplingDateSK, SamplingHour" : "SamplingDateSK";
    }

    protected _getTaskAgentRequestsAggregation(): string {
        return "Count with max as MaxCount";
    }

    protected _getTaskAgentRequestsFilter(
        confValues: ITrendChartReportConfiguration,
        poolId: number,
        hosted: boolean,
        visibility: string,
        isRunning: boolean
    ): string {
        let datefilter = `${getDateFilter(confValues.trendBy, confValues.period, "SamplingDateSK")}`;
        const lookBackDate = getLookBackDate(confValues.trendBy, confValues.period);
        const lookBackDateSK = DateSKParser.dateStringToDateSK(DateSKParser.convertDateToDateString(lookBackDate));
        if (confValues.trendBy === TrendBy.Hours) {
            datefilter = `((${datefilter} AND SamplingHour gt ${lookBackDate.getHours()}) OR SamplingDateSK gt ${lookBackDateSK})`;
        }

        /**
         * Hosted Public
         * Project/ProjectVisibility eq "Public"
         *
         * Hosted Private
         * Project/ProjectVisibility ne "Private
         *
         * SelfHosted (False) (Public or Private)
         * eq PoolId
         *
         * SelfHosted (False) Private
         * Project/ProjectVisibility ne "Private"
         */
        let scopingFilter;
        if (hosted || visibility === Visibility.Private) {
            scopingFilter = `AND Project/ProjectVisibility ${visibility === Visibility.Public ? "eq" : "ne"} '${
                Visibility.Public
            }' AND IsRunning eq ${isRunning} AND IsHosted eq ${hosted}`;
        } else {
            scopingFilter = `AND PoolId eq ${poolId} AND IsRunning eq ${isRunning}`;
        }

        return `filter(${datefilter} ${scopingFilter})`;
    }

    protected _getConcurrencyQueryOptions(
        confValues: ITrendChartReportConfiguration,
        poolId: number,
        hosted: boolean,
        visibility: string
    ): ODataQueryOptions {
        const concurrencyQueryOptions = {
            entityType: hosted || visibility === Visibility.Private ? c_ParallelPipelinesEntityName : c_TaskAgentPoolSizeEntityName,
            oDataVersion: AnalyticsODataVersion.v4Preview
        } as ODataQueryOptions;

        const lookBackDate = getLookBackDate(confValues.trendBy, confValues.period);

        if (hosted || visibility === Visibility.Private) {
            concurrencyQueryOptions.$apply = `filter(SamplingDate ge ${DateSKParser.convertDateToDateString(
                lookBackDate
            )} AND IsHosted eq ${hosted} AND ParallelismTag ${visibility === Visibility.Public ? "eq" : "ne"} '${Visibility.Public}')`;
        } else {
            concurrencyQueryOptions.$apply = `filter(SamplingDate ge ${DateSKParser.convertDateToDateString(lookBackDate)} AND PoolId eq ${poolId})`;
        }
        concurrencyQueryOptions.$orderby = `SamplingDate asc`;

        return concurrencyQueryOptions;
    }

    protected _convertSamplingHourToTimeString(samplingHour?: number) {
        return `${samplingHour || 0}:00`;
    }

    protected _adjustData<T>(data: IDataForDate<T>[], trendBy?: TrendBy, period?: Period, defaultData?: T | null): IDataForDate<T>[] {
        if (trendBy === TrendBy.Hours) {
            return this._adjustDataForHours(data, defaultData);
        } else {
            return adjustDataForAllPeriod(data, trendBy, period, defaultData);
        }
    }

    protected _adjustDataForHours<T>(data: IDataForDate<T>[], defaultData?: T | null): IDataForDate<T>[] {
        if (!data) {
            return data;
        }

        // Creating dictionary of trend date
        const dataDayToAggrMap: { [key: string]: T | null | undefined } = {};
        const dateToCustomDataMap: { [key: string]: any } = {};
        data.forEach(d => {
            if (!dataDayToAggrMap[d.date]) {
                dataDayToAggrMap[d.date] = d.data;
                dateToCustomDataMap[d.date] = d.customData;
            }
        });

        let dateStart: Date = new Date();

        dateStart = addDays(getDate(), -1, true);
        dateStart.setHours(dateStart.getHours() + 1, 0, 0, 0); 

        const adjustedData: IDataForDate<T>[] = [];

        for (let hour = 0; hour < 24; hour++) {
            let dateString = this._convertSamplingHourToTimeString(dateStart.getHours());
            if (dataDayToAggrMap.hasOwnProperty(dateString)) {
                adjustedData.push({
                    date: dateString,
                    data: dataDayToAggrMap[dateString],
                    customData: dateToCustomDataMap[dateString]
                });
            } else {
                adjustedData.push({ date: dateString, data: defaultData });
            }

            dateStart.setHours(dateStart.getHours() + 1);
        }

        return adjustedData;
    }
}

const c_TaskAgentResultEntityName = "TaskAgentRequestSnapshots";
const c_ParallelPipelinesEntityName = "ParallelPipelineJobsSnapshot";
const c_TaskAgentPoolSizeEntityName = "TaskAgentPoolSizeSnapshots";
